import { CheckmarkCircleFilledIcon, ChevronLeftIcon, EditIcon } from '@calizahq/react-components-v2'
import styled from '@emotion/styled'
import { formatInTimeZone } from 'date-fns-tz'
import { groupBy } from 'lodash'
import React, { useEffect, useMemo, useRef, useState } from 'react'

import { BottomSheet } from '@/components'
import { CreateHostRatingForm } from '@/forms/CreateHostRating/CreateHostRatingForm'
import { UpdateInstructionForm } from '@/forms/UpdateInstructionForm'
import { HomeQualityCheckMutations } from '@/graphql/mutations/homeQualityChecks.mutations'
import { useToggle } from '@/hooks'
import { AccessLevel, useAccessLevel } from '@/hooks/useAccessLevel'
import { useGeolocation } from '@/hooks/useGeolocation'
import { useGraphqlMutation } from '@/hooks/useGraphql'
import { useHomeQualityCheck } from '@/hooks/useHomeQualityChecks'
import { HomeQualityCheckStatus } from '@/types/HomeQualityCheck'
import { AccessInstruction } from '@/types/Homes'
import { KeyAccessType, KeyGroup, KeyType } from '@/types/Key'
import { formatDate, parseDate } from '@/util'
import { AccessInstructions } from '@/views/AccessInstructions/AccessInstructions'

import { HomeKeyDetails } from '../../HomeKeys/Details'
import { HomeKeySummary } from '../../HomeKeys/Summary'
import { HomeIssues } from '../HomeIssues/HomeIssues'
import { HomeReadiness } from '../HomeReadiness'
import { HostRating } from '../HostRatings/HostRating'
import { ReservationAccessCodes } from '../Reservations/ReservationAccessCodes'
import { HomeQualityCheckInspectionLocationDetails } from './Locations/Details'
import { HomeQualityCheckInspectionLocationSummary } from './Locations/Summary'
import { UpdateHomeQualityCheckInspection } from './UpdateInspection'

type Props = {
  homeId: number
  homeQualityCheckId: number
  onClearHomeQualityCheckId: () => void
}

export const HomeQualityCheckInspectionDetail = ({ homeId, homeQualityCheckId, onClearHomeQualityCheckId }: Props) => {
  const [homeLocation, setHomeLocation] = useState<string>()
  const [keyGroup, setKeyGroup] = useState<KeyGroup>()
  const { captureGeolocation } = useGeolocation()
  const hasStartedRef = useRef(false)

  const { data, refetch, isLoading } = useHomeQualityCheck({ homeQualityCheckId })
  const { mutate } = useGraphqlMutation(HomeQualityCheckMutations.startHomeQualityCheck(), {
    onSuccess: () => {
      refetch()
    },
  })

  const inspectionTasks = useMemo(() => {
    return data && data.homeQualityCheck?.inspectionTasks ? data?.homeQualityCheck?.inspectionTasks : []
  }, [data])

  const homeIssueData = useMemo(() => {
    return data && data.homeQualityCheck?.homeIssues
      ? data?.homeQualityCheck?.homeIssues.filter((homeIssue) => !homeIssue.resolvedAt)
      : []
  }, [data])

  const accessInstructions = useMemo(() => {
    return data && data.homeQualityCheck?.home?.accessInstructions
      ? data?.homeQualityCheck?.home?.accessInstructions
      : []
  }, [data])

  const inspections = useMemo(() => {
    return data && data.homeQualityCheck?.inspections ? data?.homeQualityCheck?.inspections : []
  }, [data])

  const keys = useMemo(() => {
    return data && data.homeQualityCheck?.home?.keys ? data?.homeQualityCheck?.home?.keys : []
  }, [data])

  const groupedAccessTypes = useMemo(() => {
    const accessTypes = groupBy(keys, 'accessType')
    const groupedAccessTypes = Object.entries(accessTypes).map((accessType) => {
      const keyTypes = groupBy(accessType[1], 'keyType')
      const groupedKeyTypes = Object.entries(keyTypes).map((keyType) => {
        return {
          accessType: accessType[0] as KeyAccessType,
          keyType: keyType[0] as KeyType,
          count: keyType[1].length,
        }
      })
      return groupedKeyTypes
    })
    return groupedAccessTypes.flat()
  }, [keys])

  const activeKeys = useMemo(() => {
    return keys && keyGroup
      ? keys.filter((key) => key.accessType === keyGroup?.accessType && key.keyType === keyGroup?.keyType)
      : []
  }, [keys, keyGroup])

  useEffect(() => {
    if (homeQualityCheckId && !!data && inspectionTasks.length === 0 && !hasStartedRef.current) {
      hasStartedRef.current = true
      let cancelled = false

      captureGeolocation().then((geolocation) => {
        if (!cancelled) {
          mutate({ homeQualityCheckId, geolocation })
        }
      })

      return () => {
        cancelled = true
      }
    }
  }, [data, inspectionTasks, homeQualityCheckId, mutate, captureGeolocation])

  const { AGM, HUB_MANAGER } = AccessLevel
  const { accessGranted } = useAccessLevel()
  const isEditable = accessGranted([AGM, HUB_MANAGER])
  const [editInstruction, setEditInstruction] = useState<AccessInstruction>()
  const { isShown: showModal, toggle: toggleModal } = useToggle()
  const { isShown: showHostFeedbackModal, toggle: toggleHostFeedbackModal } = useToggle()
  const { isShown: showKeyGroupModal, toggle: toggleKeyGroupModal } = useToggle()
  const { isShown: showReadinessModal, toggle: toggleReadinessModal } = useToggle()
  const { isShown: showUpdateInspection, toggle: toggleUpdateInspectionModal } = useToggle()

  const handleEditInstruction = (instruction: AccessInstruction) => {
    setEditInstruction(instruction)
    !showModal && toggleModal()
  }

  const handleCloseModal = () => {
    setEditInstruction(undefined)
    toggleModal()
  }

  const handleResetHomeLocation = () => {
    setHomeLocation(undefined)
    refetch()
  }

  const handleChangeHomeKeyGroup = (accessType: KeyAccessType, keyType: KeyType) => {
    const keyGroup = { accessType, keyType }
    setKeyGroup(keyGroup)
    toggleKeyGroupModal()
  }

  const handleCloseKeyGroupModal = () => {
    setKeyGroup(undefined)
    toggleKeyGroupModal()
  }

  return (
    <>
      {homeLocation ? (
        <HomeQualityCheckInspectionLocationDetails
          data={inspectionTasks}
          homeId={homeId}
          homeLocation={homeLocation}
          onResetHomeLocation={handleResetHomeLocation}
        />
      ) : (
        <div className="flex w-full flex-col py-4">
          <div className="items-left flex w-full flex-col justify-between md:mb-4" role="presentation">
            <div className="flex items-center">
              <button className={'mr-2 grid h-5 w-5 items-center justify-center'} onClick={onClearHomeQualityCheckId}>
                <ChevronLeftIcon className="w-5 cursor-pointer icon-tertiary" />
              </button>
              <div className="flex items-center justify-between">
                <p className="text-header-md leading-120">Home Quality Check Inspection</p>
                {data?.homeQualityCheck.status === HomeQualityCheckStatus.READY ? (
                  <CheckmarkCircleFilledIcon className="ml-2 mr-4 h-5 text-green-300" />
                ) : null}
              </div>
              <button
                className={'ml-4 mr-2 grid h-4 w-4 items-center justify-center'}
                onClick={toggleUpdateInspectionModal}
                title="Edit Home Quality Check"
              >
                <EditIcon className="text-blue-300" width={24} />
              </button>
            </div>
            <p className="text-header-sm text-tertiary mb-2">{formattedCreatedAt(data?.homeQualityCheck.createdAt)}</p>
            {data?.homeQualityCheck?.precedingReservation?.includeDamageWaiverFee ? (
              <div className={'font-bold text-semantic-success mr-2 flex h-5 w-full'}>
                <p className="text-sm leading-120 underline">Damage Waiver Included</p>
              </div>
            ) : null}
            {data?.homeQualityCheck.status === HomeQualityCheckStatus.NOT_READY ? (
              <div className="flex items-left">
                <div className={'font-bold text-semantic-success mr-2 flex h-5 w-full'}>
                  <p
                    className="text-sm leading-120 cursor-pointer hover:underline"
                    onClick={toggleReadinessModal}
                    role="presentation"
                  >
                    Mark Home as Ready
                  </p>
                </div>
              </div>
            ) : null}
          </div>

          {inspections.length > 0 ? (
            <ContainerStyled>
              {inspections.map((inspection, index) => {
                return (
                  <HomeQualityCheckInspectionLocationSummary
                    inspection={inspection}
                    key={index}
                    setHomeLocation={setHomeLocation}
                  />
                )
              })}
            </ContainerStyled>
          ) : null}

          {groupedAccessTypes.length > 0 ? (
            <ContainerStyled>
              {groupedAccessTypes.map((accessType, index) => {
                return (
                  <HomeKeySummary
                    accessType={accessType.accessType}
                    count={accessType.count}
                    key={index}
                    keyType={accessType.keyType}
                    onChangeHomeKeyGroup={handleChangeHomeKeyGroup}
                  />
                )
              })}
            </ContainerStyled>
          ) : null}

          {data && data.homeQualityCheck?.turnoverCleaningJob ? (
            <HostRating
              hostName={data.homeQualityCheck?.turnoverCleaningJob.host?.fullName}
              jobId={data.homeQualityCheck.turnoverCleaningJob.id}
              jobTemplate={data.homeQualityCheck?.turnoverCleaningJob.jobTemplate.name}
              scheduleDate={
                formatDate(parseDate(data.homeQualityCheck?.turnoverCleaningJob.scheduleDate), 'MMMM d, yyyy') ||
                'Unscheduled'
              }
              stars={data.homeQualityCheck?.hostCleaningRating || 0}
              toggleHostFeedbackModal={toggleHostFeedbackModal}
            />
          ) : null}

          <HomeIssues
            className="max-h-72 overflow-y-scroll"
            data={homeIssueData}
            isLoading={isLoading}
            refetchOnSuccess={refetch}
          />

          <AccessCodeContainerStyled>
            <ReservationAccessCodes
              allowUpdates={false}
              header="Previous Reservation Access Codes"
              reservation={data?.homeQualityCheck.precedingReservation}
            />
            <ReservationAccessCodes
              allowUpdates={!!data?.homeQualityCheck.upcomingReservation}
              header="Upcoming Reservation Access Codes"
              refetchOnSuccess={refetch}
              reservation={data?.homeQualityCheck.upcomingReservation}
            />
          </AccessCodeContainerStyled>

          <>
            <p className="text-header-md">Access Instructions</p>
            <AccessInstructions
              data={accessInstructions}
              handleEditInstruction={handleEditInstruction}
              isEditable={isEditable}
              isLoading={isLoading}
            />
          </>

          <BottomSheet header="Update Instruction" isVisible={showModal} onClose={handleCloseModal}>
            {editInstruction && (
              <UpdateInstructionForm
                accessInstruction={editInstruction}
                closeModal={handleCloseModal}
                refetchOnSuccess={refetch}
              />
            )}
          </BottomSheet>

          <BottomSheet
            header="Host Feedback"
            isVisible={showHostFeedbackModal}
            mobileFullHeight={true}
            onClose={toggleHostFeedbackModal}
          >
            <CreateHostRatingForm
              homeQualityCheckId={homeQualityCheckId}
              refetchOnSuccess={refetch}
              toggle={toggleHostFeedbackModal}
            />
          </BottomSheet>

          <BottomSheet isVisible={showKeyGroupModal} onClose={handleCloseKeyGroupModal}>
            {activeKeys.length > 0 && keyGroup ? (
              <HomeKeyDetails accessType={keyGroup.accessType} homeKeys={activeKeys} keyType={keyGroup.keyType} />
            ) : null}
          </BottomSheet>

          <BottomSheet isVisible={showReadinessModal} onClose={toggleReadinessModal}>
            <HomeReadiness
              homeQualityCheckId={homeQualityCheckId}
              onClearHomeQualityCheckId={onClearHomeQualityCheckId}
              toggle={toggleReadinessModal}
            />
          </BottomSheet>
          <BottomSheet isVisible={showUpdateInspection} onClose={toggleUpdateInspectionModal}>
            {data && data.homeQualityCheck ? (
              <UpdateHomeQualityCheckInspection
                homeQualityCheck={data.homeQualityCheck}
                refetchOnSuccess={refetch}
                toggle={toggleUpdateInspectionModal}
              />
            ) : null}
          </BottomSheet>
        </div>
      )}
    </>
  )
}

export const ContainerStyled = styled.div`
  padding: 1rem 0;
  display: grid;
  grid-area: auto / auto / auto / auto;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.5rem;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
  }
`

export const AccessCodeContainerStyled = styled.div`
  padding: 1rem 0;
  display: grid;
  grid-area: auto / auto / auto / auto;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(1, 1fr);
    gap: 1rem;
  }
`

export const formattedCreatedAt = (value: string | undefined): string => {
  if (!value) return ''
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
  try {
    const time = formatInTimeZone(value, timezone, 'MMMM d, yyyy')
    return time
  } catch (e) {
    return ''
  }
}
